// src/features/services/media/hooks/mixEngine/orchestrator/useSpeedDsp.ts

import { useEffect, useRef } from 'react';
import type { TransportClock } from '../../../scheduler/clock';
import { computeSpeedMapping } from '../speedMapping';
import { L } from '../../../utils/logger';
import { openGateWhenSignal } from './helpers/health';
import { makeTransportGate } from './helpers/transportGate';

interface Ctx {
  refs: any;
  tracks: Array<{ role: string; url: string }>;
  bufferModeRef: React.MutableRefObject<boolean>;
  scheduledRef: React.MutableRefObject<Record<string, any> | null>;
  logicalStartRef: React.MutableRefObject<number>;
  startedAtRef: React.MutableRefObject<number>;
  masterPlaybackRate: number;
  status: 'idle' | 'loading' | 'playing' | 'paused';
  maintainPitch: boolean;
  pauseAll: () => void;
  scheduleStartAll: (
    ctx: AudioContext,
    scheduled: any,
    whenDelta: number,
    offset: number,
    rate: number,
    perRoleOffset?: Record<string, number>,
  options?: { seamlessSwap?: boolean; fixedLatencySec?: number; clock?: TransportClock },
  ) => number;
  setStatus: (s: any) => void;
  getEffectiveGain: (role: string) => number;
  resumeAfterLoadRef: React.MutableRefObject<boolean>;
  userPausedRef: React.MutableRefObject<boolean>;
}

export function useSpeedDsp(ctx: Ctx) {
  const {
    refs, tracks, bufferModeRef, scheduledRef, logicalStartRef, startedAtRef,
    masterPlaybackRate, status, maintainPitch, scheduleStartAll,
    resumeAfterLoadRef, userPausedRef,
  } = ctx;

  // Debounce timer for direct playbackRate pushes
  const prDebounceRef = (refs as any)._prDebounceRef || ((refs as any)._prDebounceRef = { id: 0 as any, target: 1 as number });

  // Track previous state to prevent unnecessary reschedules
  const prevRef = useRef({ rate: masterPlaybackRate, mp: maintainPitch });

  // Debounced push of playbackRate to all active sources (always push WA rate).
  const debouncedPushPlaybackRate = (target: number) => {
    try { prDebounceRef.target = target; } catch {}
    try { if (prDebounceRef.id) clearTimeout(prDebounceRef.id); } catch {}
    prDebounceRef.id = setTimeout(() => {
      const actx: AudioContext | null = refs.audioCtxRef?.current ?? null;
      const sched = scheduledRef.current;
      if (!actx || !sched) return;
      try {
        for (const s of Object.values(sched)) {
          const stem: any = s;
          if (!stem?.source) continue;
          const pr: AudioParam | undefined = (stem.source as any)?.playbackRate;
          if (!pr) continue;
          try {
            const now = actx.currentTime;
            pr.cancelScheduledValues(now);
            pr.setValueAtTime(target, now);
          } catch {}
        }
      } catch {}
    }, 100);
  };

  // Helper: re-anchor the transport clock once using prior anchor if present
  // Policy: only invoke on play/seek or when rate actually changes; not per-tick.
  const reanchorClock = (actx: AudioContext) => {
    const clk = (refs as any).transportClockRef?.current as TransportClock | null | undefined;
    const mediaNow = clk
      ? clk.t0Media + (actx.currentTime - clk.t0Audio) * clk.rate
      : logicalStartRef.current;
    (refs as any).transportClockRef = (refs as any).transportClockRef || { current: null };
    (refs as any).transportClockRef.current = {
      t0Audio: actx.currentTime,
      t0Media: mediaNow,
      rate: masterPlaybackRate,
    } as TransportClock;
  try { L.info('media:transport', 'clock.anchor', (refs as any).transportClockRef.current); } catch {}
  };

  // Unified effect: only reschedule on maintainPitch changes; rate-only updates adjust tempo or playbackRate
  useEffect(() => {
  if (!tracks.length) return;
    const playingOrGate = (status === 'playing' || resumeAfterLoadRef.current) && !userPausedRef.current;
    if (!playingOrGate) return;

    const actx: AudioContext | null = refs.audioCtxRef.current;
    const sched = scheduledRef.current;
  if (!bufferModeRef.current || !actx || !sched) return;
  const anyRbPresent = Object.values(sched || {}).some((s: any) => !!(s?.worklet?.node));

  const prev = prevRef.current;
    const rateChanged = Math.abs(masterPlaybackRate - prev.rate) > 1e-3;
    const mpChanged = maintainPitch !== prev.mp;
    if (!rateChanged && !mpChanged) return;
    // Log only when a meaningful change occurs
    try { L.info('media:transport', 'speed.change', { maintainPitch, rate: masterPlaybackRate, ctxT: ctx.refs?.audioCtxRef?.current?.currentTime ?? 0 }); } catch {}
    prevRef.current = { rate: masterPlaybackRate, mp: maintainPitch };

    (async () => {
      const prevRate = (refs as any)._prevRateRef?.current ?? 1;
      if (mpChanged) {
        try {
          if (maintainPitch) {
            const { ensureRubberBandWorklet } = await import('../bufferTransport/worklets');
            await ensureRubberBandWorklet(actx, sched);
            Object.values(sched).forEach((s: any) => { s.useWorklet = true; });

            const elapsed = Math.max(0, actx.currentTime - startedAtRef.current) * prevRate;
            logicalStartRef.current = logicalStartRef.current + elapsed;
            startedAtRef.current = actx.currentTime;
            reanchorClock(actx);
            const fn = (refs as any).reanchorClockAndReschedule as (() => number | void) | undefined;
            // Gate master during reschedule
            try { makeTransportGate(refs).closeTransportGate(); } catch {}
            if (typeof fn === 'function') fn(); else {
              const hidden = (() => { try { return typeof document !== 'undefined' && document.visibilityState !== 'visible'; } catch { return false; } })();
              const la = hidden ? 0.25 : 0.08;
              scheduleStartAll(actx, sched, la, logicalStartRef.current, masterPlaybackRate, undefined, { seamlessSwap: true, fixedLatencySec: hidden ? 0.08 : 0.04, clock: (refs as any).transportClockRef?.current });
            }
            try { openGateWhenSignal(refs); } catch {}
            // Initialize mapping according to current rate
            try {
              // Even if RB hasn't reported live yet, compute mapping and push WA playbackRate immediately
              const anyRbNow = !!scheduledRef.current && Object.values(scheduledRef.current).some((s: any) => !!(s as any)?._rbLive || !!(s?.worklet?.node as any)?.__live);
              if (!anyRbNow) {
                // Warn once but do not early-return; we'll still set WA playbackRate per mapping
                try { L.warn('media:transport', 'rb.attach_pending_fallback'); } catch {}
              }
              const map = computeSpeedMapping(masterPlaybackRate, true);
              const st = (refs as any).pitchStRef?.current ?? 0;
              const userPitch = Math.pow(2, (Number(st) || 0) / 12);
              if (masterPlaybackRate <= 1.0 + 1e-3) {
                for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(map.rbTempo); (s as any).worklet?.setPitch?.(userPitch); }
                (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null };
                (refs as any).rbTempoRef.current = map.rbTempo;
              } else {
                const combined = map.rbPitch * userPitch;
                for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(combined); }
                (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null };
                (refs as any).rbTempoRef.current = 1.0;
              }
              try { (window as any).__rbLastPitch = userPitch; } catch {}
              // Ensure WA playbackRate matches mapping immediately (prevents lingering pitch from prior mode)
              try {
                const now = actx.currentTime;
                for (const s of Object.values(sched)) {
                  const stem: any = s;
                  const pr: AudioParam | undefined = (stem.source as any)?.playbackRate;
                  if (pr) {
                    pr.cancelScheduledValues(now);
                    pr.setValueAtTime(map.webAudioRate, now);
                  }
                }
              } catch {}
              // Re-apply once shortly after to catch late RB-attach and fully normalize pitch
              try {
                setTimeout(() => {
                  try {
                    const map2 = computeSpeedMapping(masterPlaybackRate, true);
                    const st2 = (refs as any).pitchStRef?.current ?? 0;
                    const userPitch2 = Math.pow(2, (Number(st2) || 0) / 12);
                    if (masterPlaybackRate <= 1.0 + 1e-3) {
                      for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(map2.rbTempo); (s as any).worklet?.setPitch?.(userPitch2); }
                      (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null };
                      (refs as any).rbTempoRef.current = map2.rbTempo;
                    } else {
                      const combined2 = map2.rbPitch * userPitch2;
                      for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(combined2); }
                      (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null };
                      (refs as any).rbTempoRef.current = 1.0;
                    }
                  } catch {}
                }, 40);
              } catch {}
            } catch {}
          } else {
            Object.values(sched).forEach((s: any) => { s.useWorklet = false; });
            const elapsed = Math.max(0, actx.currentTime - startedAtRef.current) * prevRate;
            logicalStartRef.current = logicalStartRef.current + elapsed;
            startedAtRef.current = actx.currentTime;
            reanchorClock(actx);
            const fn = (refs as any).reanchorClockAndReschedule as (() => number | void) | undefined;
            try { makeTransportGate(refs).closeTransportGate(); } catch {}
            if (typeof fn === 'function') fn(); else {
              const hidden = (() => { try { return typeof document !== 'undefined' && document.visibilityState !== 'visible'; } catch { return false; } })();
              const la = hidden ? 0.25 : 0.08;
              scheduleStartAll(actx, sched, la, logicalStartRef.current, masterPlaybackRate, undefined, { seamlessSwap: true, fixedLatencySec: 0, clock: (refs as any).transportClockRef?.current });
            }
            try { openGateWhenSignal(refs); } catch {}
            // Neutralize RB engine state
            try { for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(1.0); (s as any).worklet?.setPitch?.(1.0); } } catch {}
            // Ensure WebAudio playbackRate on all active sources matches current rate after mode flip
            try {
              for (const s of Object.values(sched)) {
                const stem: any = s;
                const pr: AudioParam | undefined = (stem.source as any)?.playbackRate;
                if (pr) {
                  const now = actx.currentTime;
                  pr.cancelScheduledValues(now);
                  pr.setValueAtTime(masterPlaybackRate, now);
                }
              }
            } catch {}
          }
        } catch {}
  } else if (rateChanged) {
        // If pitch-preserve is requested but no RB engine is wired, attempt to attach it now
        if (maintainPitch && !anyRbPresent) {
          try {
            const { ensureRubberBandWorklet } = await import('../bufferTransport/worklets');
            await ensureRubberBandWorklet(actx, sched);
            Object.values(sched).forEach((s: any) => { s.useWorklet = true; });
          } catch {
            // Fallback: keep WA playbackRate in sync, pitch will be neutral until RB comes up
            debouncedPushPlaybackRate(masterPlaybackRate);
            (refs as any)._rbMissingWarned ||= false;
            if (!(refs as any)._rbMissingWarned) {
              try { L.warn('media:transport', 'rb.missing_fallback'); } catch {}
              (refs as any)._rbMissingWarned = true;
            }
          }
        }
        // Rate-only change: keep timeline continuous, no reschedule
        const elapsed = Math.max(0, actx.currentTime - startedAtRef.current) * prevRate;
        logicalStartRef.current = logicalStartRef.current + elapsed;
        startedAtRef.current = actx.currentTime;
  // Re-anchor clock so future mediaNow uses the new rate from 'now'
  reanchorClock(actx);
        if (maintainPitch && anyRbPresent) {
          const map = computeSpeedMapping(masterPlaybackRate, true);
          // For r<=1, we drive RB tempo; for r>1, we neutralize tempo and drive pitch
          if (masterPlaybackRate <= 1.0 + 1e-3) {
            const tempo = map.rbTempo;
            try { for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(tempo); } } catch {}
            try { (window as any).__rbLastTempo = tempo; } catch {}
            try { (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null }; (refs as any).rbTempoRef.current = tempo; } catch {}
            // Apply user pitch in stretch regime
            const st = (refs as any).pitchStRef?.current ?? 0;
            const userPitch = Math.pow(2, (Number(st) || 0) / 12);
            try { for (const s of Object.values(sched)) { (s as any).worklet?.setPitch?.(userPitch); } } catch {}
            try { (window as any).__rbLastPitch = userPitch; } catch {}
            // Also ensure WA playbackRate matches mapping for existing sources
            try {
              for (const s of Object.values(sched)) {
                const stem: any = s;
                const pr: AudioParam | undefined = (stem.source as any)?.playbackRate;
                if (pr) {
                  try {
                    const now = actx.currentTime;
                    pr.cancelScheduledValues(now);
                    pr.setValueAtTime(map.webAudioRate, now);
                  } catch {}
                }
              }
            } catch {}
          } else {
            // Fast path: WA plays faster; RB fixes pitch
            try { for (const s of Object.values(sched)) { (s as any).worklet?.setTempo?.(1.0); } } catch {}
            const st2 = (refs as any).pitchStRef?.current ?? 0;
            const userPitch = Math.pow(2, (Number(st2) || 0) / 12);
            const combined = map.rbPitch * userPitch;
            try { for (const s of Object.values(sched)) { (s as any).worklet?.setPitch?.(combined); } } catch {}
            try { (window as any).__rbLastPitch = userPitch; } catch {}
            try { (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null }; (refs as any).rbTempoRef.current = 1.0; } catch {}
            // Ensure existing sources also run faster now (even with worklet path)
            try {
              for (const s of Object.values(sched)) {
                const stem: any = s;
                const pr: AudioParam | undefined = (stem.source as any)?.playbackRate;
                if (pr) {
                  try {
                    const now = actx.currentTime;
                    pr.cancelScheduledValues(now);
                    pr.setValueAtTime(map.webAudioRate, now);
                  } catch {}
                }
              }
            } catch {}
          }
        } else {
          debouncedPushPlaybackRate(masterPlaybackRate);
        }
        (refs as any)._prevRateRef = (refs as any)._prevRateRef || { current: 1 };
        (refs as any)._prevRateRef.current = masterPlaybackRate;
      }
    })();
  }, [maintainPitch, masterPlaybackRate, status, tracks, refs, bufferModeRef, scheduledRef, logicalStartRef, startedAtRef, scheduleStartAll, resumeAfterLoadRef, userPausedRef]);
}
